import React, { useEffect, useMemo, useState } from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

import PageLayout from "examples/LayoutContainers/PageLayout";
import { API_URL } from "config";

const tableCellStyle = {
  border: "1px solid #4b5563",
  padding: "8px",
  fontSize: 13,
  textAlign: "center",
  verticalAlign: "middle",
};

const headerCellStyle = {
  ...tableCellStyle,
  background: "#4f86c6",
  color: "#fff",
  fontWeight: 700,
};

const inputStyle = {
  width: "100%",
  minWidth: 120,
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  padding: "8px",
  font: "inherit",
};

const digitsOnly = (value, maxDigits) => String(value || "").replace(/\D/g, "").slice(0, maxDigits);

const integerInput = (value, maxDigits) => {
  const integerPart = String(value ?? "").split(".")[0];

  return integerPart.replace(/\D/g, "").slice(0, maxDigits);
};

const decimalInput = (value, maxIntegerDigits, maxDecimalDigits) => {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = cleaned.split(".");
  const integer = integerPart.slice(0, maxIntegerDigits);

  if (decimalParts.length === 0) return integer;

  const decimal = decimalParts.join("").slice(0, maxDecimalDigits);
  return `${integer}.${decimal}`;
};

const normalizeResponseValue = (value) => {
  const text = String(value ?? "").trim();

  return text === "" ? "0" : text;
};

const normalizeIntegerResponseValue = (value) => {
  const text = integerInput(value, 7);

  return text === "" ? "0" : text;
};

function VendorFeasibility() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [payload, setPayload] = useState(null);
  const [vendorResponses, setVendorResponses] = useState({});
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        setError("Invalid RFQ link.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/vendor-feasibility?token=${encodeURIComponent(token)}`, {
          headers: { Accept: "application/json" },
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || "Unable to open RFQ link.");
        }

        const nextVendorResponses = {};
        (data.bid?.details || []).forEach((detail) => {
          nextVendorResponses[detail.id] = {
            target_cpi: decimalInput(detail.target_cpi, 3, 2),
            feasibility: digitsOnly(detail.feasibility, 7),
            required_field_time_days: integerInput(detail.required_field_time_days, 7),
          };
        });

        setPayload(data);
        setVendorResponses(nextVendorResponses);
        setNotes(data.invite?.notes || "");
      } catch (loadError) {
        setError(loadError.message || "Unable to open RFQ link.");
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
  }, [token]);

  const details = payload?.bid?.details || [];
  const alreadySubmitted = Boolean(payload?.invite?.submitted);

  const updateVendorResponse = (detailId, field, value) => {
    const nextValue =
      field === "target_cpi"
        ? decimalInput(value, 3, 2)
        : field === "required_field_time_days"
          ? integerInput(value, 7)
          : digitsOnly(value, 7);

    setVendorResponses((current) => ({
      ...current,
      [detailId]: {
        ...(current[detailId] || {}),
        [field]: nextValue,
      },
    }));
  };

  const requestSubmit = () => {
    const invalidTargetCpi = details.some((detail) => {
      const value = String(vendorResponses[detail.id]?.target_cpi || "").trim();
      return value !== "" && !/^\d{1,3}(\.\d{1,2})?$/.test(value);
    });
    if (invalidTargetCpi) {
      setMessage("");
      setError("Target CPI must be numeric with maximum 3 digits and 2 decimals.");
      return;
    }

    const invalidFeasibility = details.some((detail) => {
      const value = String(vendorResponses[detail.id]?.feasibility || "").trim();
      return value !== "" && !/^\d{1,7}$/.test(value);
    });
    if (invalidFeasibility) {
      setMessage("");
      setError("Feasibility must be numeric and maximum 7 digits.");
      return;
    }

    const invalidFieldTime = details.some((detail) => {
      const value = String(vendorResponses[detail.id]?.required_field_time_days || "").trim();
      return value !== "" && !/^\d{1,7}$/.test(value);
    });
    if (invalidFieldTime) {
      setMessage("");
      setError("Required Field Time must be numeric and maximum 7 digits.");
      return;
    }

    setError("");
    setConfirmOpen(true);
  };

  const submitFeasibility = async () => {
    setSubmitting(true);
    setConfirmOpen(false);

    try {
      const response = await fetch(`${API_URL}/vendor-feasibility`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          notes,
          feasibilities: details.map((detail) => {
            const response = vendorResponses[detail.id] || {};

            return {
              bid_detail_id: detail.id,
              target_cpi: normalizeResponseValue(response.target_cpi),
              feasibility: normalizeResponseValue(response.feasibility),
              required_field_time_days: normalizeIntegerResponseValue(response.required_field_time_days),
            };
          }),
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Unable to submit feasibility.");
      }

      setMessage(
        data.message ||
          "Your feasibility has been successfully submitted. We will revert back you very soon."
      );
      setPayload((current) => ({
        ...current,
        invite: {
          ...(current?.invite || {}),
          submitted: true,
          submitted_at: new Date().toISOString(),
        },
      }));
    } catch (submitError) {
      setError(submitError.message || "Unable to submit feasibility.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout background="white">
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "Arial, Helvetica, sans-serif" }}>
          <CircularProgress />
        </div>
      </PageLayout>
    );
  }

  if (error && !payload) {
    return (
      <PageLayout background="white">
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, fontFamily: "Arial, Helvetica, sans-serif" }}>
          <div style={{ maxWidth: 520, textAlign: "center" }}>
            <h2 style={{ margin: "0 0 10px", color: "#111827" }}>RFQ Link</h2>
            <p style={{ margin: 0, color: "#b91c1c" }}>{error}</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout background="white">
      <div style={{ minHeight: "100vh", background: "#fff", padding: "32px 24px", fontFamily: "Arial, Helvetica, sans-serif", color: "#222" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <p style={{ fontSize: 15, lineHeight: 1.5, margin: "0 0 18px" }}>
          <strong>Hi Team,</strong>
        </p>

        <p style={{ fontSize: 15, lineHeight: 1.55, margin: "0 0 16px" }}>
          We have been awarded this study and would like to bring you onboard. Please provide a quote for the criteria listed below and let us know if you can meet our target CPI.
        </p>

        <p style={{ fontSize: 15, lineHeight: 1.55, margin: "0 0 28px" }}>
          Please quote using your proprietary panel only.
        </p>

        <div style={{ overflowX: "auto", marginBottom: 28 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #4b5563" }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Country/Geos</th>
                <th style={headerCellStyle}>Target</th>
                <th style={headerCellStyle}>LOI(mins)</th>
                <th style={headerCellStyle}>IR(%)</th>
                <th style={headerCellStyle}>Req. N</th>
                <th style={headerCellStyle}>Target CPI ($)</th>
                <th style={headerCellStyle}>Feasibility</th>
                <th style={headerCellStyle}>Required Field Time (Days)</th>
              </tr>
            </thead>
            <tbody>
              {details.map((detail) => (
                <tr key={detail.id}>
                  <td style={tableCellStyle}>{detail.country_name || "-"}</td>
                  <td style={{ ...tableCellStyle, textAlign: "left" }}>{detail.targeting_text || "-"}</td>
                  <td style={tableCellStyle}>{detail.loi ?? "-"}</td>
                  <td style={tableCellStyle}>{detail.ir !== null && detail.ir !== undefined ? `${detail.ir}%` : "-"}</td>
                  <td style={tableCellStyle}>{detail.required_n ?? "-"}</td>
                  <td style={tableCellStyle}>
                    <input
                      style={inputStyle}
                      inputMode="decimal"
                      maxLength={6}
                      placeholder="0"
                      value={vendorResponses[detail.id]?.target_cpi || ""}
                      disabled={alreadySubmitted || submitting}
                      onChange={(event) => updateVendorResponse(detail.id, "target_cpi", event.target.value)}
                    />
                  </td>
                  <td style={tableCellStyle}>
                    <input
                      style={inputStyle}
                      inputMode="numeric"
                      maxLength={7}
                      placeholder="0"
                      value={vendorResponses[detail.id]?.feasibility || ""}
                      disabled={alreadySubmitted || submitting}
                      onChange={(event) => updateVendorResponse(detail.id, "feasibility", event.target.value)}
                    />
                  </td>
                  <td style={tableCellStyle}>
                    <input
                      style={inputStyle}
                      inputMode="numeric"
                      maxLength={7}
                      placeholder="0"
                      value={vendorResponses[detail.id]?.required_field_time_days || ""}
                      disabled={alreadySubmitted || submitting}
                      onChange={(event) =>
                        updateVendorResponse(detail.id, "required_field_time_days", event.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ fontSize: 15, lineHeight: 1.5, margin: "0 0 8px" }}>
          <strong><u>Additional Notes:</u></strong>
        </p>
        <p style={{ fontSize: 15, lineHeight: 1.5, margin: "0 0 22px" }}>
          {payload?.bid?.comments || "N/A"}
        </p>

        <TextField
          label="General Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={alreadySubmitted || submitting}
          multiline
          minRows={4}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: { xs: "100%", md: "50%" },
            mb: 2,
            "& .MuiInputBase-root": {
              width: "100%",
              alignItems: "flex-start",
            },
            "& textarea": {
              width: "100% !important",
              minWidth: "100% !important",
              boxSizing: "border-box",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "normal",
            },
          }}
        />

        {payload?.invite?.expires_at && (
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 16px" }}>
            Link valid until {new Date(payload.invite.expires_at).toLocaleString("en-IN")}.
          </p>
        )}

        {error && <p style={{ color: "#b91c1c", fontSize: 14, margin: "0 0 14px" }}>{error}</p>}
        {message && <p style={{ color: "#15803d", fontSize: 14, margin: "0 0 14px" }}>{message}</p>}

        <Button
          variant="contained"
          color="success"
          disabled={alreadySubmitted || submitting}
          onClick={requestSubmit}
          endIcon={submitting ? <CircularProgress color="inherit" size={18} /> : null}
        >
          {alreadySubmitted ? "Submitted" : submitting ? "Submitting..." : "Submit"}
        </Button>
        </div>

        <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            Are you sure you want to submit this feasibility?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="contained" color="success" onClick={submitFeasibility} disabled={submitting}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </PageLayout>
  );
}

export default VendorFeasibility;
